import { useEffect, useState, type ComponentType } from "react";

const importToasters = async () => {
  const [{ Toaster: AppToaster }, { Toaster: SonnerToaster }] = await Promise.all([
    import("@/components/ui/toaster"),
    import("@/components/ui/sonner"),
  ]);

  const Combined = () => (
    <>
      <AppToaster />
      <SonnerToaster />
    </>
  );

  return Combined;
};

export function DeferredToasters() {
  const [Component, setComponent] = useState<ComponentType | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;
    let idleId: number | null = null;
    let loadHandler: (() => void) | null = null;
    const win = window as typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    let scheduled = false;

    const load = () => {
      if (cancelled) return;
      importToasters().then((Combined) => {
        if (cancelled) return;
        setComponent(() => Combined);
      });
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      if (typeof win.requestIdleCallback === "function") {
        idleId = win.requestIdleCallback(() => load(), { timeout: 1500 });
      } else {
        timeoutId = window.setTimeout(load, 800);
      }
    };

    if (document.readyState === "complete" || document.readyState === "interactive") {
      schedule();
    } else {
      loadHandler = () => schedule();
      window.addEventListener("load", loadHandler, { once: true });
      schedule();
    }

    return () => {
      cancelled = true;
      if (loadHandler) {
        window.removeEventListener("load", loadHandler);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (idleId !== null && typeof win.cancelIdleCallback === "function") {
        win.cancelIdleCallback(idleId);
      }
    };
  }, []);

  if (!Component) return null;
  return <Component />;
}
